import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import styles from './Chip.module.css';

export type ChipTone = 'primary' | 'secondary' | 'neutral' | 'success';

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  selected?: boolean;
  tone?: ChipTone;
  bordered?: boolean;
  children: ReactNode;
}

/** Selectable pill (mobile/src/components/ui/Chip.tsx): a toggle button with `aria-pressed`. */
export function Chip({
  selected = false,
  tone = 'primary',
  bordered = false,
  type = 'button',
  className,
  children,
  ...rest
}: ChipProps) {
  return (
    <button
      {...rest}
      type={type}
      aria-pressed={selected}
      className={[styles.chip, className].filter(Boolean).join(' ')}
      data-tone={tone}
      data-bordered={bordered || undefined}
    >
      {children}
    </button>
  );
}

export type BadgeVariant = 'trust' | 'success' | 'primary' | 'secondary' | 'neutral' | 'danger';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  small?: boolean;
  children: ReactNode;
}

/** Pill badge (the design's `.badge-trust` and colour variants; mobile/src/components/ui/Badge.tsx). */
export function Badge({
  variant = 'trust',
  small = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      {...rest}
      className={[styles.badge, className].filter(Boolean).join(' ')}
      data-variant={variant}
      data-small={small || undefined}
    >
      {children}
    </span>
  );
}
